import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { setSession } from '../middleware/auth';

const mono = { fontFamily: "'DM Mono', monospace" };
const sans = { fontFamily: "'DM Sans', sans-serif" };
const serif = { fontFamily: "'Cormorant Garamond', serif" };

function generateAccessCode(clientName, clientPhone) {
  if (!clientName || !clientPhone) return null;
  const parts = clientName.trim().split(/\s+/);
  if (parts.length < 2) return null;
  const initials = (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  const digits = clientPhone.replace(/\D/g, '');
  if (digits.length < 4) return null;
  return initials + digits.slice(-4);
}

export default function ClientLogin() {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [btnHover, setBtnHover] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = code.trim().toUpperCase();
    if (!trimmed) return;
    setError('');
    setSubmitting(true);

    try {
      // Try upload_token match first
      let { data } = await supabase
        .from('cordis_engagements')
        .select('id,client_name,client_phone,upload_token')
        .eq('upload_token', trimmed);

      let matched = data && data.length > 0 ? data[0] : null;

      // If no upload_token match, try computed access code
      if (!matched) {
        const { data: all } = await supabase
          .from('cordis_engagements')
          .select('id,client_name,client_phone,upload_token');

        if (all) {
          matched = all.find((eng) => {
            const computed = generateAccessCode(
              eng.client_name || '',
              eng.client_phone || ''
            );
            return computed && computed === trimmed;
          }) || null;
        }
      }

      if (!matched) {
        setError('Invalid access code. Please check your welcome email or contact your advisor.');
        setSubmitting(false);
        return;
      }

      setSession('client', matched.id, matched.upload_token || '');
      localStorage.setItem('cordis_client_engagement_id', matched.id);
      navigate('/portal');
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const labelStyle = {
    ...mono,
    fontSize: 8,
    letterSpacing: '0.18em',
    textTransform: 'uppercase',
    color: '#7A5230',
    marginBottom: 6,
  };

  const inputStyle = {
    ...sans,
    fontSize: 14,
    padding: '12px 16px',
    border: '1px solid rgba(10,24,40,0.15)',
    width: '100%',
    outline: 'none',
    background: '#FFFFFF',
    minHeight: 48,
    boxSizing: 'border-box',
    color: '#0A1828',
  };

  const dividerStyle = {
    height: 1,
    background: 'rgba(10,24,40,0.10)',
    border: 'none',
    margin: 0,
  };

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      background: '#0A1828',
      padding: 20,
    }}>
      <form
        onSubmit={handleSubmit}
        style={{
          width: '100%',
          maxWidth: 400,
          background: '#FAFAF8',
          padding: 48,
          boxSizing: 'border-box',
        }}
      >
        <div style={{
          ...mono,
          fontSize: 9,
          letterSpacing: '0.28em',
          color: '#7A5230',
          textTransform: 'uppercase',
          marginBottom: 8,
        }}>
          CORDIS GROUP
        </div>

        <div style={{
          ...serif,
          fontSize: 36,
          fontWeight: 300,
          color: '#0A1828',
          lineHeight: 1.1,
          marginBottom: 32,
        }}>
          Client Portal
        </div>

        <hr style={{ ...dividerStyle, marginBottom: 32 }} />

        <div style={{ marginBottom: 20 }}>
          <div style={labelStyle}>NAME</div>
          <input
            autoComplete="name"
            style={inputStyle}
            onFocus={(e) => { e.target.style.borderColor = '#7A5230'; }}
            onBlur={(e) => { e.target.style.borderColor = 'rgba(10,24,40,0.15)'; }}
          />
        </div>

        <div style={{ marginBottom: 24 }}>
          <div style={labelStyle}>ACCESS CODE</div>
          <input
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            autoFocus
            autoComplete="off"
            style={{
              ...inputStyle,
              ...mono,
              fontSize: 14,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
            }}
            onFocus={(e) => { e.target.style.borderColor = '#7A5230'; }}
            onBlur={(e) => { e.target.style.borderColor = 'rgba(10,24,40,0.15)'; }}
          />
        </div>

        {error && (
          <div style={{
            ...sans,
            fontSize: 13,
            color: 'rgba(160,40,40,0.85)',
            marginBottom: 16,
            lineHeight: 1.5,
          }}>
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting || !code.trim()}
          onMouseEnter={() => setBtnHover(true)}
          onMouseLeave={() => setBtnHover(false)}
          style={{
            ...mono,
            fontSize: 9,
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            background: btnHover && !submitting ? '#8C6240' : '#7A5230',
            color: '#FFFFFF',
            border: 'none',
            borderRadius: 0,
            height: 48,
            width: '100%',
            cursor: submitting ? 'not-allowed' : 'pointer',
            opacity: submitting || !code.trim() ? 0.6 : 1,
            transition: 'background 0.15s ease',
          }}
        >
          {submitting ? 'Verifying' : 'Access Portal'}
        </button>

        <div style={{
          ...mono,
          fontSize: 9,
          color: 'rgba(10,24,40,0.45)',
          textAlign: 'center',
          marginTop: 16,
          lineHeight: 1.6,
        }}>
          Your access code was provided in your welcome email.
        </div>

        <hr style={{ ...dividerStyle, marginTop: 32, marginBottom: 24 }} />

        <div style={{
          ...mono,
          fontSize: 8,
          letterSpacing: '0.18em',
          color: 'rgba(10,24,40,0.25)',
          textAlign: 'center',
          textTransform: 'uppercase',
        }}>
          CONFIDENTIAL · CORDIS GROUP LLC
        </div>
      </form>
    </div>
  );
}
